import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useAudit } from "../contexts/auditContext";
import { isScreenshotChildType } from "../lib/documentChildTypes";
import { type AuditSession } from "../models/audit/auditSession";
import { Citation } from "../models/audit/citation";
import { citationToBoundingBox } from "../pages/audit/auditWorkPaperViewV2/components/attributeTestAnalysis/types";
import { findTrailDisplayItem } from "../pages/audit/auditWorkPaperViewV2/lib/trailDisplay";
import { useAttributeTestData } from "./useAttributeTestData";
import { useCitationActions } from "./useCitationActions";
import { useCitationNavigation } from "./useCitationNavigation";

interface UseAttributeTestHighlightProps {
  auditSession: AuditSession;
  checkResultId?: string;
  sourceRowId?: string;
}

/**
 * Headless orchestration for the attribute-test fold-out's evidence experience.
 * The doc viewer + citation actions operate on the AUDITOR-TRAIL citations —
 * the decisive evidence the agent attached to each L2 step
 * (`auditorTrail.steps[].citations`) — NOT the reperformance set. Owns the
 * citation-navigation state and exposes `handleCitationClick`, which lights up
 * the shared document viewer (it reads the active document/URL from
 * `useAudit()`). Used only by `AttributeTestFoldOut`.
 */
export function useAttributeTestHighlight({
  auditSession,
  checkResultId,
  sourceRowId,
}: UseAttributeTestHighlightProps) {
  const { controlSession, updateCurrentDocument } = useAudit();
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);

  const { attributeTestResult, sourceRow, columnName, attributeColumnCode } =
    useAttributeTestData({
      auditSession,
      checkResultId,
      sourceRowId,
      controlSession,
    });

  const auditorTrail = attributeTestResult?.auditorTrail;

  // The trail's citations, flattened in step order — the set the doc viewer's
  // overlay, selection, and edit actions all key off.
  const trailCitations = useMemo(
    () => auditorTrail?.steps.flatMap((step) => step.citations) ?? [],
    [auditorTrail],
  );

  // A document is a screenshot child (e.g. `excel_screenshot`). Used to default a
  // source/screenshot pair to its screenshot view.
  const isScreenshotDoc = useCallback(
    (documentId: string | null | undefined) => {
      if (!documentId) return false;
      return isScreenshotChildType(
        auditSession.documents.find((d) => d.documentId === documentId)
          ?.fetchableDocument?.childType,
      );
    },
    [auditSession.documents],
  );

  const onCitationSelectRef = useRef<(citation: Citation) => void>(() => {});
  const navigation = useCitationNavigation({
    filteredCitations: trailCitations,
    onCitationSelect: (citation) => onCitationSelectRef.current(citation),
    // The trail's arrow-key nav lives in `AuditorTrailPanel`, tethered to the
    // distinct evidence items so one press = one move. Disable this hook's
    // flat-citation-list handler so the two don't both fire.
    keyboardEnabled: false,
  });

  // On open, land on the first citation of the first FAILED (exception) L2 step
  // so a reviewer starts on the deviation; falls back to the first citation when
  // nothing failed. The flat trail-citation index of that step's first citation:
  const initialEvidenceIndex = useMemo(() => {
    if (!auditorTrail) return 0;
    let idx = 0;
    for (const step of auditorTrail.steps) {
      if (step.isException && step.citations.length > 0) return idx;
      idx += step.citations.length;
    }
    return 0;
  }, [auditorTrail]);
  // Runs once per mounted fold-out (the parent re-keys per attribute), so it sets
  // the opening selection without yanking the cursor during manual navigation.
  const didInitialSelect = useRef(false);
  useEffect(() => {
    if (didInitialSelect.current || trailCitations.length === 0) return;
    didInitialSelect.current = true;
    if (initialEvidenceIndex > 0) {
      navigation.setActiveEvidenceIndex(initialEvidenceIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trailCitations.length, initialEvidenceIndex]);

  const updateDocumentForCitation = useCallback(
    (citation: Citation, preferScreenshot: boolean = true) => {
      // A source/screenshot pair defaults to the screenshot view, so an implicit
      // caller (initial auto-select, arrow-key nav, row click) that lands on the
      // live source resolves to the rendered screenshot instead of e.g. the xlsx.
      // `showCitationView` passes `preferScreenshot=false` to show the requested
      // citation as-is, so the viewer's toggle can switch to the live source.
      const item = preferScreenshot
        ? findTrailDisplayItem(auditorTrail, citation.id, isScreenshotDoc)
        : null;
      const viewCitation = item?.screenshot ?? citation;
      const sourceDocument = auditSession.documents.find(
        (doc) => doc.documentId === viewCitation.documentId,
      );
      if (!sourceDocument) return;

      navigation.setCurrentSelectionValue(
        viewCitation.shortSummary || undefined,
      );
      navigation.setCurrentBoundingBox(citationToBoundingBox(viewCitation));

      updateCurrentDocument({
        id: sourceDocument.id,
        documentId: sourceDocument.documentId,
        fileName: sourceDocument.name || undefined,
        fileType: sourceDocument.fetchableDocument?.fileType,
        fetchableDocument: sourceDocument.fetchableDocument || undefined,
        isMissing: false,
        type: sourceDocument.fetchableDocument?.fileType,
        procedureSessionId: auditSession.id,
      });
      setShowDocumentViewer(true);
    },
    [
      auditSession.id,
      auditSession.documents,
      updateCurrentDocument,
      navigation,
      auditorTrail,
      isScreenshotDoc,
    ],
  );

  // Keep the selection ref pointed at the latest callback BEFORE passive effects
  // run, so the navigation hook's first auto-select (which fires in a passive
  // effect) opens the doc instead of hitting the initial no-op.
  useLayoutEffect(() => {
    onCitationSelectRef.current = updateDocumentForCitation;
  }, [updateDocumentForCitation]);

  const handleCitationClick = useCallback(
    (citation: Citation) => {
      const idx = trailCitations.findIndex((c) => c.id === citation.id);
      if (idx >= 0) {
        navigation.setActiveEvidenceIndex(idx);
      }
      updateDocumentForCitation(citation);
    },
    [trailCitations, navigation, updateDocumentForCitation],
  );

  // Re-land on the opening selection (first citation of the first failed L2 step,
  // else the first citation) — the same evidence a fresh cell click would show.
  // Used to restore the trail's default focus when switching back from Agent
  // Steps, which had pointed the viewer at a reperformance citation.
  const selectInitialEvidence = useCallback(() => {
    const citation = trailCitations[initialEvidenceIndex];
    if (!citation) return;
    navigation.setActiveEvidenceIndex(initialEvidenceIndex);
    updateDocumentForCitation(citation);
  }, [
    trailCitations,
    initialEvidenceIndex,
    navigation,
    updateDocumentForCitation,
  ]);

  // The viewer's screenshot/source toggle: switch the current evidence's view
  // WITHOUT moving the evidence cursor. Changing the active index would re-fire
  // the navigation effect (which always re-resolves to the screenshot default)
  // and race the document back, so the toggle re-points the document directly
  // and shows the requested citation as-is.
  const showCitationView = useCallback(
    (citation: Citation) => updateDocumentForCitation(citation, false),
    [updateDocumentForCitation],
  );

  const actions = useCitationActions({
    auditSession,
    sourceRowId,
    checkResultId,
    attributeTestResult,
    allCitations: trailCitations,
    filteredCitations: trailCitations,
    activeEvidenceIndex: navigation.activeEvidenceIndex,
    currentBoundingBox: navigation.currentBoundingBox,
    setCurrentBoundingBox: navigation.setCurrentBoundingBox,
    setActiveEvidenceIndex: navigation.setActiveEvidenceIndex,
  });

  return {
    attributeTestResult,
    auditorTrail,
    sourceRow,
    columnName,
    attributeColumnCode,
    allCitations: trailCitations,
    isScreenshotDoc,
    navigation,
    actions,
    showDocumentViewer,
    handleCitationClick,
    selectInitialEvidence,
    showCitationView,
  };
}
